import { useState } from 'react';
import {
  useSubscription,
  usePricing,
  formatPrice,
  formatInterval,
} from '@shadeables/billing';
import { UpgradeModal } from '@shadeables/ui';
import styles from './SettingsBilling.module.css';

const SettingsBilling: React.FC = () => {
  const {
    subscription,
    plan,
    isActive,
    isPastDue,
    isTrialing,
    willCancelAtPeriodEnd,
    manageBilling,
    isLoading,
  } = useSubscription();

  const { pricingPlans } = usePricing();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      await manageBilling();
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleUpgrade = async () => {
    // Find the pro monthly price
    const proMonthlyPrice = pricingPlans.find((p) =>
      p.name.toLowerCase().includes('pro')
    )?.monthlyPrice;

    if (proMonthlyPrice) {
      // This would typically use useStripeCheckout hook
      console.log('Redirecting to checkout for price:', proMonthlyPrice.id);
      // For demo purposes, we'll just close the modal
      setShowUpgradeModal(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Loading billing information...
      </div>
    );
  }

  return (
    <div className={styles.settingsBilling}>
      <h1>Billing & Subscription</h1>

      {/* Current Plan Section */}
      <div
        style={{
          background: 'var(--color-background-secondary)',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
        }}
      >
        <h2>Current Plan</h2>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <p
              style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                margin: '0.5rem 0',
              }}
            >
              {plan === 'pro' ? 'Pro Plan' : 'Free Plan'}
            </p>
            {subscription && (
              <div>
                <p
                  style={{
                    margin: '0.25rem 0',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Status:{' '}
                  <span
                    style={{
                      color: isActive
                        ? '#10b981'
                        : isPastDue
                        ? '#f59e0b'
                        : '#6b7280',
                      fontWeight: 'bold',
                    }}
                  >
                    {isActive
                      ? 'Active'
                      : isPastDue
                      ? 'Past Due'
                      : subscription.status}
                  </span>
                </p>
                {subscription.current_period_end && (
                  <p
                    style={{
                      margin: '0.25rem 0',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {willCancelAtPeriodEnd ? 'Cancels' : 'Renews'} on:{' '}
                    {new Date(
                      subscription.current_period_end
                    ).toLocaleDateString()}
                  </p>
                )}
                {isTrialing && (
                  <p style={{ margin: '0.25rem 0', color: '#10b981' }}>
                    ✨ Free trial active
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            {plan === 'free' ? (
              <button
                onClick={() => setShowUpgradeModal(true)}
                style={{
                  background:
                    'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Upgrade to Pro
              </button>
            ) : (
              <button
                onClick={handleManageBilling}
                disabled={billingLoading}
                style={{
                  background: 'transparent',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: billingLoading ? 'not-allowed' : 'pointer',
                  opacity: billingLoading ? 0.7 : 1,
                }}
              >
                {billingLoading ? 'Loading...' : 'Manage Billing'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Plan Features */}
      <div style={{ marginBottom: '2rem' }}>
        <h2>Plan Features</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
          }}
        >
          {pricingPlans.map((planItem) => (
            <div
              key={planItem.id}
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '1.5rem',
                background:
                  plan === 'pro' && planItem.name.toLowerCase().includes('pro')
                    ? 'var(--color-background-secondary)'
                    : 'transparent',
              }}
            >
              <h3 style={{ margin: '0 0 1rem 0' }}>{planItem.name}</h3>
              <p
                style={{
                  color: 'var(--color-text-secondary)',
                  margin: '0 0 1rem 0',
                }}
              >
                {planItem.description}
              </p>

              {planItem.monthlyPrice && (
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {formatPrice(planItem.monthlyPrice.unit_amount)}
                  </span>
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    /{formatInterval(planItem.monthlyPrice.interval)}
                  </span>
                </div>
              )}

              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {planItem.name.toLowerCase().includes('free') ? (
                  <>
                    <li
                      style={{
                        padding: '0.25rem 0',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      ❌ 1 project limit
                    </li>
                    <li
                      style={{
                        padding: '0.25rem 0',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      ❌ No 3D shaders
                    </li>
                    <li
                      style={{
                        padding: '0.25rem 0',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      ❌ Basic export only
                    </li>
                  </>
                ) : (
                  <>
                    <li style={{ padding: '0.25rem 0', color: '#10b981' }}>
                      ✅ Unlimited projects
                    </li>
                    <li style={{ padding: '0.25rem 0', color: '#10b981' }}>
                      ✅ 3D shader access
                    </li>
                    <li style={{ padding: '0.25rem 0', color: '#10b981' }}>
                      ✅ Advanced export
                    </li>
                    <li style={{ padding: '0.25rem 0', color: '#10b981' }}>
                      ✅ Priority support
                    </li>
                  </>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Pro Features"
        onUpgrade={handleUpgrade}
      />
    </div>
  );
};

export default SettingsBilling;
